import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { db } from "../supabase";
import { fehlertext } from "../fehler";
import { istIos, pushAusschalten, pushEinschalten, pushTesten, pushZustand } from "../push";
import { Karte } from "../ui/Karte";
import { Knopf } from "../ui/Knopf";
import { Meldung } from "../ui/Leer";
import { useHinweis } from "../ui/Hinweise";
import ui from "../ui/ui.module.css";
import s from "./seiten.module.css";

const DATUM = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Berlin" });

/** Profil, Push: auf diesem Gerät einrichten (iPhone: erst installieren), Geräte mit Push ansehen und entfernen (BAUPLAN-ABSCHLUSS.md, Abschnitt 6). */
export function PushEinrichten() {
  const qc = useQueryClient();
  const { zeigen } = useHinweis();
  const [laeuft, setLaeuft] = useState(false);
  const zustand = useQuery({ queryKey: ["push", "zustand"], queryFn: pushZustand, staleTime: 0 });
  const geraete = useQuery({
    queryKey: ["push", "geraete"],
    queryFn: async () => {
      const { data, error } = await db().from("app_push_abos").select("id, endpoint, geraet, erstellt_am, zuletzt_genutzt_am").order("erstellt_am", { ascending: false });
      if (error) throw error;
      return data;
    },
  });
  const neu = () => void qc.invalidateQueries({ queryKey: ["push"] });
  // Links auf /profil#push (Mitteilungen, Banner zur Installation): zur Karte springen, sobald sie steht.
  const { hash } = useLocation();
  useEffect(() => {
    if (hash === "#push") document.getElementById("push")?.scrollIntoView({ block: "start" });
  }, [hash]);

  async function einschalten() {
    setLaeuft(true);
    try {
      await pushEinschalten();
      zeigen("Push ist auf diesem Gerät an.");
    } catch (e) {
      zeigen(fehlertext(e), { fehler: true });
    } finally {
      setLaeuft(false);
      neu();
    }
  }
  const aus = useMutation({
    mutationFn: ({ id, endpoint }: { id: number; endpoint: string }) => pushAusschalten(id, endpoint),
    onSuccess: () => zeigen("Gerät entfernt."),
    onError: (e) => zeigen(fehlertext(e), { fehler: true }),
    onSettled: neu,
  });
  const test = useMutation({
    mutationFn: pushTesten,
    onSuccess: (n) => zeigen(n === 1 ? "Test-Push ist unterwegs." : `Test-Push ist unterwegs an ${n} Geräte.`),
    onError: (e) => zeigen(fehlertext(e), { fehler: true }),
    onSettled: neu,
  });

  const z = zustand.data?.zustand;
  const dieses = zustand.data?.endpoint ?? null;
  const diesesGeraet = geraete.data?.find((g) => g.endpoint === dieses);

  return (
    <Karte titel="Push auf dem Handy" id="push">
      {zustand.isPending ? <p>Prüfe dieses Gerät …</p> : null}
      {z === "nicht_unterstuetzt" ? <p>Dieser Browser kann keine Push-Mitteilungen. Am Handy geht es mit Safari (iPhone, installierte App) oder Chrome (Android).</p> : null}
      {z === "ios_installieren" ? (
        <>
          <p>Auf dem iPhone geht Push nur in der installierten App. Die Reihenfolge ist wichtig:</p>
          <ol className={s.schritte}>
            <li>In Safari unten auf „Teilen“ tippen.</li>
            <li>„Zum Home-Bildschirm“ wählen und hinzufügen.</li>
            <li>Die obsn App vom Home-Bildschirm öffnen und dort neu anmelden.</li>
            <li>In der App im Profil „Push einschalten“.</li>
          </ol>
        </>
      ) : null}
      {z === "abgelehnt" ? (
        <Meldung fehler>
          Push ist für app.obsn.de abgelehnt. {istIos() ? "Erlauben kannst du es wieder unter Einstellungen, Mitteilungen, obsn." : "Erlauben kannst du es wieder über das Schloss neben der Adresse, Benachrichtigungen."} Danach diese Seite neu laden.
        </Meldung>
      ) : null}
      {z === "aus" ? (
        <>
          <p>Bekomm Zuweisungen, Erwähnungen, „Frist morgen“ und Überfälliges als Push. Bei Aufgaben „Nur Vorstand“ steht nie der Titel auf dem Sperrbildschirm, nie ein Kommentartext.</p>
          <div>
            <Knopf art="primaer" onClick={() => void einschalten()} disabled={laeuft}>
              {laeuft ? "Einen Moment …" : "Push einschalten"}
            </Knopf>
          </div>
        </>
      ) : null}
      {z === "an" ? (
        <>
          <Meldung>Push ist auf diesem Gerät an.</Meldung>
          <div className={s.zeile} style={{ marginTop: 12 }}>
            <Knopf onClick={() => test.mutate()} disabled={test.isPending}>
              Test-Push
            </Knopf>
            {diesesGeraet ? (
              <Knopf art="leise" onClick={() => aus.mutate({ id: diesesGeraet.id, endpoint: diesesGeraet.endpoint })} disabled={aus.isPending}>
                Auf diesem Gerät ausschalten
              </Knopf>
            ) : (
              <Knopf onClick={() => void einschalten()} disabled={laeuft}>
                Neu verbinden
              </Knopf>
            )}
          </div>
        </>
      ) : null}

      {geraete.data && geraete.data.length ? (
        <>
          <h3 className={s.unterTitel}>Deine Geräte mit Push</h3>
          <ul className={s.einstellungen}>
            {geraete.data.map((g) => (
              <li key={g.id}>
                <span>
                  {g.geraet || "Gerät"}
                  {g.endpoint === dieses ? <strong> (dieses Gerät)</strong> : null}
                  <small className={ui.hilfe} style={{ display: "block" }}>
                    seit {DATUM.format(new Date(g.erstellt_am))}
                    {g.zuletzt_genutzt_am ? `, zuletzt ${DATUM.format(new Date(g.zuletzt_genutzt_am))}` : ""}
                  </small>
                </span>
                <Knopf klein art="leise" onClick={() => aus.mutate({ id: g.id, endpoint: g.endpoint })} disabled={aus.isPending}>
                  Entfernen
                </Knopf>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </Karte>
  );
}
